/**
 * EmailSnippetEditor Component
 * Comprehensive email snippet editor with rich text body, variable support,
 * HTML/plain text toggle, preview mode, and test send functionality
 */

import { useState, useCallback, useMemo, useEffect } from 'react'
import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Placeholder from '@tiptap/extension-placeholder'
import CharacterCount from '@tiptap/extension-character-count'
import Highlight from '@tiptap/extension-highlight'
import Link from '@tiptap/extension-link'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link as LinkIcon,
  Undo,
  Redo,
  Eye,
  Edit3,
  Send,
  Code2,
  FileText,
  Mail,
  Braces,
} from 'lucide-react'
import { SubjectInput, EmailPreview, TestSendDialog } from './email'
import { VariablePicker, type Variable } from './VariablePicker'
import type { EmailSnippetContent } from '@/lib/api/types'

// ============================================
// Types
// ============================================

export interface EmailSnippetEditorProps {
  value: Partial<EmailSnippetContent>
  onChange: (content: Partial<EmailSnippetContent>) => void
  disabled?: boolean
  className?: string
  customVariables?: Variable[]
}

// ============================================
// Toolbar Button Component
// ============================================

interface ToolbarButtonProps {
  onClick: () => void
  active?: boolean
  disabled?: boolean
  tooltip: string
  children: React.ReactNode
}

function ToolbarButton({
  onClick,
  active = false,
  disabled = false,
  tooltip,
  children,
}: ToolbarButtonProps) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClick}
            disabled={disabled}
            className={cn(
              'h-8 w-8 p-0 rounded-md',
              active && 'bg-neutral-200 dark:bg-neutral-700'
            )}
          >
            {children}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// ============================================
// Formatting Toolbar Component
// ============================================

interface FormattingToolbarProps {
  editor: Editor | null
  onInsertVariable: (variable: Variable) => void
  disabled?: boolean
  customVariables?: Variable[]
}

function FormattingToolbar({
  editor,
  onInsertVariable,
  disabled = false,
  customVariables = [],
}: FormattingToolbarProps) {
  if (!editor) return null

  return (
    <div className="flex items-center gap-0.5 flex-wrap p-1.5 border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 rounded-t-xl">
      {/* Text Formatting */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive('bold')}
        disabled={disabled}
        tooltip="Bold (Ctrl+B)"
      >
        <Bold className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive('italic')}
        disabled={disabled}
        tooltip="Italic (Ctrl+I)"
      >
        <Italic className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        active={editor.isActive('underline')}
        disabled={disabled}
        tooltip="Underline (Ctrl+U)"
      >
        <UnderlineIcon className="h-4 w-4" />
      </ToolbarButton>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Lists */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive('bulletList')}
        disabled={disabled}
        tooltip="Bullet List"
      >
        <List className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive('orderedList')}
        disabled={disabled}
        tooltip="Numbered List"
      >
        <ListOrdered className="h-4 w-4" />
      </ToolbarButton>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Link */}
      <ToolbarButton
        onClick={() => {
          const previousUrl = editor.getAttributes('link').href
          const url = window.prompt('URL:', previousUrl)

          if (url === null) return

          if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run()
            return
          }

          editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
        }}
        active={editor.isActive('link')}
        disabled={disabled}
        tooltip="Insert Link"
      >
        <LinkIcon className="h-4 w-4" />
      </ToolbarButton>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Undo/Redo */}
      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={disabled || !editor.can().undo()}
        tooltip="Undo (Ctrl+Z)"
      >
        <Undo className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={disabled || !editor.can().redo()}
        tooltip="Redo (Ctrl+Y)"
      >
        <Redo className="h-4 w-4" />
      </ToolbarButton>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Variable Picker */}
      <VariablePicker
        onSelect={onInsertVariable}
        customVariables={customVariables}
        disabled={disabled}
      />
    </div>
  )
}

// ============================================
// Editor Stats Component
// ============================================

interface EditorStatsProps {
  characters: number
  words: number
  variables: string[]
}

function EditorStats({ characters, words, variables }: EditorStatsProps) {
  return (
    <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400 px-3 py-2 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 rounded-b-xl">
      <div className="flex items-center gap-4">
        <span>{characters.toLocaleString()} characters</span>
        <span>{words.toLocaleString()} words</span>
      </div>
      {variables.length > 0 && (
        <div className="flex items-center gap-1.5">
          <Braces className="h-3.5 w-3.5" />
          <span>
            {variables.length} variable{variables.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}
    </div>
  )
}

// ============================================
// Helper Functions
// ============================================

function extractVariables(content: string): string[] {
  const regex = /\{\{(\w+)\}\}/g
  const matches: string[] = []
  let match: RegExpExecArray | null
  while ((match = regex.exec(content)) !== null) {
    const varName = match[1]
    if (varName && !matches.includes(varName)) {
      matches.push(varName)
    }
  }
  return matches
}

function countWords(text: string): number {
  return text
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length
}

// ============================================
// EmailSnippetEditor Component
// ============================================

export function EmailSnippetEditor({
  value,
  onChange,
  disabled = false,
  className,
  customVariables = [],
}: EmailSnippetEditorProps) {
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit')
  const [testSendOpen, setTestSendOpen] = useState(false)

  // Determine if HTML mode
  const isHtml = value.bodyFormat === 'Html'

  // Initialize TipTap editor for HTML mode
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Highlight,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 dark:text-blue-400 underline',
        },
      }),
      Placeholder.configure({
        placeholder: 'Write your email body here...',
        emptyEditorClass: 'is-editor-empty',
      }),
      CharacterCount,
    ],
    content: isHtml ? value.body || '' : '',
    editable: !disabled && isHtml,
    onUpdate: ({ editor }) => {
      if (isHtml) {
        const html = editor.getHTML()
        onChange({
          ...value,
          body: html,
        })
      }
    },
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm prose-neutral dark:prose-invert max-w-none',
          'focus:outline-none',
          'p-4',
          'min-h-[200px]'
        ),
      },
    },
  })

  // Update editor content when value changes externally or format changes
  useEffect(() => {
    if (editor && isHtml) {
      const currentContent = editor.getHTML()
      if (value.body !== currentContent) {
        editor.commands.setContent(value.body || '')
      }
    }
  }, [editor, value.body, isHtml])

  // Update editor editable state
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled && isHtml && activeTab === 'edit')
    }
  }, [editor, disabled, isHtml, activeTab])

  // Handle variable insertion in HTML editor
  const handleInsertVariable = useCallback(
    (variable: Variable) => {
      if (!editor || !isHtml) return
      const variableText = `{{${variable.name}}}`
      editor.chain().focus().insertContent(variableText).run()
    },
    [editor, isHtml]
  )

  // Handle body format toggle
  const handleFormatChange = useCallback(
    (format: 'Plain' | 'Html') => {
      if (format === value.bodyFormat) return

      // Convert content between formats
      let newBody = value.body || ''
      if (format === 'Plain' && editor) {
        // Convert HTML to plain text
        newBody = editor.getText()
      }

      onChange({
        ...value,
        bodyFormat: format,
        body: newBody,
      })
    },
    [value, onChange, editor]
  )

  // Extract all variables
  const allVariables = useMemo(() => {
    const vars: string[] = []
    if (value.subject) {
      extractVariables(value.subject).forEach((v) => {
        if (!vars.includes(v)) vars.push(v)
      })
    }
    if (value.body) {
      extractVariables(value.body).forEach((v) => {
        if (!vars.includes(v)) vars.push(v)
      })
    }
    if (value.signature) {
      extractVariables(value.signature).forEach((v) => {
        if (!vars.includes(v)) vars.push(v)
      })
    }
    return vars
  }, [value.subject, value.body, value.signature])

  // Calculate stats
  const bodyText = editor?.getText() || value.body || ''
  const characters = bodyText.length
  const words = countWords(bodyText)

  return (
    <div className={cn('space-y-4', className)}>
      {/* Category and Tone Selectors */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Category</Label>
          <Select
            value={value.category || 'Body'}
            onValueChange={(category) =>
              onChange({
                ...value,
                category: category as EmailSnippetContent['category'],
              })
            }
            disabled={disabled}
          >
            <SelectTrigger className="rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Greeting">Greeting</SelectItem>
              <SelectItem value="Body">Body</SelectItem>
              <SelectItem value="Closing">Closing</SelectItem>
              <SelectItem value="Signature">Signature</SelectItem>
              <SelectItem value="Full">Full Email</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Tone</Label>
          <Select
            value={value.tone || 'Professional'}
            onValueChange={(tone) =>
              onChange({
                ...value,
                tone: tone as EmailSnippetContent['tone'],
              })
            }
            disabled={disabled}
          >
            <SelectTrigger className="rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Formal">Formal</SelectItem>
              <SelectItem value="Professional">Professional</SelectItem>
              <SelectItem value="Casual">Casual</SelectItem>
              <SelectItem value="Friendly">Friendly</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Subject Line (for Full emails) */}
      {value.category === 'Full' && (
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-neutral-500" />
            Subject Line
          </Label>
          <SubjectInput
            value={value.subject || ''}
            onChange={(subject) => onChange({ ...value, subject })}
            placeholder="Enter email subject with {{variables}}"
            disabled={disabled}
            customVariables={customVariables}
          />
        </div>
      )}

      {/* Body Editor with Tabs */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-neutral-500" />
            Email Body
          </Label>
          <div className="flex items-center gap-2">
            {/* Format Toggle */}
            <div className="flex items-center rounded-lg border border-neutral-200 dark:border-neutral-700 p-0.5">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleFormatChange('Plain')}
                className={cn(
                  'h-7 px-2.5 rounded-md text-xs gap-1.5',
                  !isHtml && 'bg-neutral-200 dark:bg-neutral-700'
                )}
                disabled={disabled}
              >
                <FileText className="h-3.5 w-3.5" />
                Plain
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleFormatChange('Html')}
                className={cn(
                  'h-7 px-2.5 rounded-md text-xs gap-1.5',
                  isHtml && 'bg-neutral-200 dark:bg-neutral-700'
                )}
                disabled={disabled}
              >
                <Code2 className="h-3.5 w-3.5" />
                HTML
              </Button>
            </div>

            {/* Edit/Preview Tabs */}
            <Tabs
              value={activeTab}
              onValueChange={(v) => setActiveTab(v as 'edit' | 'preview')}
            >
              <TabsList className="h-8 p-0.5 rounded-lg">
                <TabsTrigger
                  value="edit"
                  className="h-7 px-2.5 text-xs gap-1.5 rounded-md"
                >
                  <Edit3 className="h-3.5 w-3.5" />
                  Edit
                </TabsTrigger>
                <TabsTrigger
                  value="preview"
                  className="h-7 px-2.5 text-xs gap-1.5 rounded-md"
                >
                  <Eye className="h-3.5 w-3.5" />
                  Preview
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        {/* Editor / Preview Content */}
        {activeTab === 'edit' ? (
          <div
            className={cn(
              'border border-neutral-200 dark:border-neutral-700 rounded-xl overflow-hidden',
              'bg-white dark:bg-neutral-950'
            )}
          >
            {isHtml ? (
              <>
                <FormattingToolbar
                  editor={editor}
                  onInsertVariable={handleInsertVariable}
                  disabled={disabled}
                  customVariables={customVariables}
                />
                <EditorContent editor={editor} />
                <EditorStats
                  characters={characters}
                  words={words}
                  variables={allVariables}
                />
              </>
            ) : (
              <div className="space-y-0">
                {/* Plain text toolbar with just variable picker */}
                <div className="flex items-center p-1.5 border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 rounded-t-xl">
                  <VariablePicker
                    onSelect={(variable) => {
                      const variableText = `{{${variable.name}}}`
                      onChange({
                        ...value,
                        body: (value.body || '') + variableText,
                      })
                    }}
                    customVariables={customVariables}
                    disabled={disabled}
                  />
                </div>
                <Textarea
                  value={value.body || ''}
                  onChange={(e) => onChange({ ...value, body: e.target.value })}
                  placeholder="Write your email body here. Use {{variableName}} for dynamic content..."
                  disabled={disabled}
                  rows={10}
                  className="border-0 rounded-none resize-none focus-visible:ring-0"
                />
                <EditorStats
                  characters={(value.body || '').length}
                  words={countWords(value.body || '')}
                  variables={allVariables}
                />
              </div>
            )}
          </div>
        ) : (
          <EmailPreview
            subject={value.subject}
            body={value.body || ''}
            signature={value.signature}
            isHtml={isHtml}
            category={value.category}
            tone={value.tone}
          />
        )}
      </div>

      {/* Signature (for Full or Signature category) */}
      {(value.category === 'Full' || value.category === 'Signature') && (
        <div className="space-y-2">
          <Label>Signature</Label>
          <Textarea
            value={value.signature || ''}
            onChange={(e) => onChange({ ...value, signature: e.target.value })}
            placeholder="Enter your email signature..."
            disabled={disabled}
            rows={3}
            className="rounded-xl resize-none"
          />
        </div>
      )}

      {/* Language */}
      <div className="space-y-2">
        <Label>Language (optional)</Label>
        <Input
          type="text"
          placeholder="e.g., en-US, de-DE"
          value={value.language || ''}
          onChange={(e) => onChange({ ...value, language: e.target.value })}
          disabled={disabled}
          className="rounded-xl max-w-xs"
        />
      </div>

      {/* Test Send Button */}
      <div className="pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => setTestSendOpen(true)}
          disabled={disabled || !value.body}
          className="rounded-xl gap-2"
        >
          <Send className="h-4 w-4" />
          Send Test Email
        </Button>
      </div>

      {/* Test Send Dialog */}
      <TestSendDialog
        open={testSendOpen}
        onOpenChange={setTestSendOpen}
        subject={value.subject}
        body={value.body || ''}
        signature={value.signature}
        isHtml={isHtml}
      />

      {/* Editor Styles */}
      <style>{`
        .ProseMirror {
          outline: none;
        }

        .ProseMirror p.is-editor-empty:first-child::before {
          color: #adb5bd;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }

        .ProseMirror p {
          margin: 0.5rem 0;
        }

        .ProseMirror p:first-child {
          margin-top: 0;
        }

        .ProseMirror p:last-child {
          margin-bottom: 0;
        }

        .ProseMirror ul,
        .ProseMirror ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }

        .ProseMirror li {
          margin: 0.25rem 0;
        }
      `}</style>
    </div>
  )
}

export default EmailSnippetEditor
